/**
 * Building the live proposal: the redline as it would look applied to the
 * document right now.
 *
 * `ProposalBuilder.build` takes the bookmarked range as Word reads it today,
 * works out which live paragraph each redline paragraph maps to, merges the
 * redline's text with whatever the paragraph now says, runs the paragraph
 * engine on each, and places whole inserted paragraphs. The result is a
 * package Word can render as a preview and, on Apply, insert. One builder is
 * one author and one stamp, so every change in the package shares the date
 * Apply later uses to recognise its own revisions.
 *
 * Revision ids restart at `FIRST_REVISION_ID` in every edited paragraph and
 * run from `INSERTED_PARAGRAPH_FIRST_ID` in steps of `INSERTED_PARAGRAPH_ID_STEP`
 * for inserted paragraphs. Word renumbers every `w:id` when a package is
 * inserted, so ids only have to be unique within one paragraph; the separate
 * range for inserted paragraphs keeps them unique inside the inserted
 * paragraph as well, where the engine's ids from a neighbouring edit would
 * otherwise repeat.
 */
import { wordDiff } from "./text/word-diff";
import { OoxmlRange } from "./ooxml-range";
import { type AcceptedTextSpan, FIRST_REVISION_ID, type OwnChangeWriting, RedlineEngine, type RevertTargetsOfTheRun } from "./redline-engine";
import { RevertTargetMarks } from "./revert-target-marks";
import type { ParagraphInsertion } from "./redline-source";
import type { RedlineTextRevert } from "./wire/redline-payload";
import { type LinkInstruction, NO_LINK_INSTRUCTIONS } from "./wire/link-instruction";
import { ParagraphModel } from "./ooxml/paragraph-model";
import { ParagraphXml } from "./ooxml/paragraph-xml";
import { Revision } from "./ooxml/revision";
import { ProposalRegionReferee, type RegionPathBuild, type RegionVerdict } from "./proposal-region-referee";
import { threeWayMerge } from "./text/three-way-merge";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "./text/word-tokenizer";

export interface ProposalInput {
    currentRangeOoxml: string;
    /**
     * What the caller does with `packageOoxml`.
     *
     * CASES
     *   - "rangeReplacement"  -> `Range.insertOoxml(..., replace)` over part of a live
     *                            document, so a comment the range anchors at one end
     *                            only would be minted a second time
     *   - "wholeDocumentSave" -> written back as the document itself, where an
     *                            unbalanced marker is the document's own state and stays
     */
    deliveredAs: "rangeReplacement" | "wholeDocumentSave";
    /** What each paragraph the redline covers should read, in order. */
    textsProposed: string[];
    /**
     * How many of the read's paragraphs are the document's, the card's texts being given to those alone
     * (`OoxmlRange.paragraphCountBeforeWordsClosingParagraph`).
     *
     * NULL
     *   Absent for every read that holds the document's paragraphs alone: all of them count.
     *   Set for Word's export as given (`AnchoredRead.wordsExportAsGiven`), which still ends with the
     *   empty paragraph Word ends its export with.
     */
    documentParagraphCount?: number;
    /** The span of each live paragraph the redline covers, or null to cover whole paragraphs. */
    coveredSpans: AcceptedTextSpan[] | null;
    insertions: ParagraphInsertion[];
    /** What each covered paragraph read when the redline was drafted, or null when the redline was drafted against the live text. */
    textsWhenProposed: string[] | null;
    /** The pending changes the redline reverts (`RedlineTextRevert`), applied to their paragraphs before the text redlines; empty for every other redline. */
    reverts: RedlineTextRevert[];
    /**
     * The text the card writes into each paragraph it names, by the paragraph's bookmark,
     * handed to each revert so a revert of an insertion knows whether the card writes words
     * into the gap it leaves (`RevertChangeRequest.textTheCardWritesAfter`).
     *
     * NULL
     *   Absent for a caller that has no card texts by bookmark: decision 5 holds for its reverts.
     */
    cardTextByBookmark?: ReadonlyMap<string, string>;
    /**
     * What the card says becomes of each link whose words it changes, by the bookmark of
     * the paragraph the link sits in (`RedlineTextParagraph.linkInstructions`), handed to
     * the engine's redline of that paragraph (`ParagraphRedlineRequest.linkInstructions`).
     *
     * NULL
     *   Absent for a caller that writes words no card instructed (a user's edit laid over a
     *   card, a markup card): every link the edits reach is judged by its words.
     */
    linkInstructionsByBookmark?: ReadonlyMap<string, readonly LinkInstruction[]>;
    /**
     * How an edit or a revert that reaches the author's own pending change is written: as Word writes it
     * for the document, as a tracked change for a preview (`OwnChangeWriting`).
     */
    ownChangeWriting: OwnChangeWriting;
    /**
     * The marks the Apply All run laid on the page as first read, which the range handed in
     * still carries: every revert is resolved by them (`RevertChangeRequest.markOfTheRun`).
     *
     * NULL
     *   Absent for a build of its own (a single Apply, a preview, a group composed into one
     *   card): the build marks its reverts' changes on the range as handed in, and takes the
     *   marks off once its reverts are written.
     */
    revertTargetsOfTheRun?: RevertTargetsOfTheRun;
}

/**
 * Where the revision ids of a paragraph's reverts start: their own band below the
 * text redline's (`FIRST_REVISION_ID`, 9000 and up), so a paragraph that is both
 * reverted and redlined carries two revisions with distinct ids.
 */
export const REVERT_FIRST_REVISION_ID = 8500;

/**
 * How far apart the revision id bands of one card's reverts start, so the ids
 * one revert stamps never meet the next revert's. The web stamps the same bands
 * when it reverts for the card's preview, so the card and the document agree.
 *
 * EXAMPLE
 *   revertIndex = 1 -> ids from 8500 + 1 × 50 = 8550, clear of the first revert's band
 */
export const REVERT_REVISION_ID_STEP = 50;

export interface ProposalOutcome {
    /**
     * The range as the card leaves it.
     *
     * WHY each layer strips the marks it laid (`RevertTargetMarks`)
     *   The builder never returns a mark it laid itself: it strips its own marks once its
     *   reverts are written, whether they all were or one threw. The Apply All fold is the one
     *   owner of the marks it passes in (`revertTargetsOfTheRun`): they travel only between the
     *   fold's own builds, and the fold strips them before the merge writer.
     *
     * CASES
     *   - the fold passed its marks in and the card built -> the fold's marks, still on the changes,
     *                                                        for the cards the fold lays after
     *   - the card could not be placed exactly            -> no mark: the range as it was read, stripped
     *   - every other build                               -> no mark
     */
    packageOoxml: string;
    changeCount: number;
    paragraphCount: number;
    /** The revision date shared by every change in the package. */
    stampedAt: string;
    /** The region fork's verdict when a covered span was passed (the add-in's build); null when the whole paragraphs were built once, as the web and core build. */
    regionVerdict: RegionVerdict | null;
}

/** One path's build of a card over the reverted package: the fork's input to the referee, with what the referee needs and what the picked outcome carries. */
interface RegionPathBuildOutcome {
    packageOoxml: string;
    changeCount: number;
    paragraphCount: number;
    /** Each paragraph of the result with every change accepted. */
    acceptedTexts: string[];
    /** The text each live paragraph was edited toward: for the whole-paragraph path, the intended texts. */
    textsToWrite: string[];
    /**
     * Per live paragraph, whether the text the path edited reads, collapsed, as the
     * text the card was drafted against: for the offset path, a span that still
     * covers the card's own words, which the path is then trusted on.
     */
    spanReadsAsBase: boolean[];
}

/** What one path of the fork needs: the package with the card's restores already applied, the card, and the spans the path edits (null: whole paragraphs). */
interface RegionPathInput {
    revertedRangeOoxml: string;
    input: ProposalInput;
    measuredSpans: AcceptedTextSpan[] | null;
    reverted: AppliedReverts;
}

/** One path's build timed and caught: what the referee judges, plus the outcome to use when the referee picks it. */
interface TimedRegionPath extends RegionPathBuild {
    outcome: RegionPathBuildOutcome | null;
}

/** What the reverts of a card did to the range: the tracked changes they wrote, and the index of every live paragraph they rewrote. */
interface AppliedReverts {
    changeCount: number;
    paragraphIndexes: Set<number>;
}

/** One build's reverts with the range they are written on and the marks they are resolved by (`ProposalBuilder.writeReverts`). */
interface RevertsToWrite {
    range: OoxmlRange;
    reverts: RedlineTextRevert[];
    ownChangeWriting: OwnChangeWriting;
    cardTextByBookmark: ReadonlyMap<string, string>;
    targetsOfTheRun: RevertTargetsOfTheRun;
    /** Grows as each revert is written. */
    applied: AppliedReverts;
}

/** For each live paragraph, the text it should become; and for each desired paragraph, the live paragraph that received its first word. */
interface ParagraphAlignment {
    targetTextPerLiveParagraph: string[];
    liveIndexOfDesired: number[];
}

/** Where a unit stands among the paragraphs being aligned: which paragraph and which of its units. */
interface UnitPlace {
    paragraphIndex: number;
    unitIndex: number;
}

/**
 * One token of the paragraphs being aligned, diffed as one stream: a redline
 * diff unit of a paragraph, or the boundary between two paragraphs (`place` null).
 */
interface AlignmentToken {
    comparisonKey: string;
    place: UnitPlace | null;
}

/**
 * A desired unit poured into a live paragraph's bucket, and, when it matched a
 * live unit word for word, where that live unit stands, so the live text's own
 * separator can stand between two desired units that never stood side by side.
 */
interface BucketUnit {
    desiredPlace: UnitPlace;
    livePlace: UnitPlace | null;
}

/**
 * Whether two texts say the same thing whatever their spacing: their words compared
 * under the tokenizer's one fold (`WordTokenizer.readsTheSame`), as the engine's own
 * already-reads-as-requested short-cut compares them.
 *
 * EXAMPLE
 *   "Nestle\u0301  shall pay"  "Nestl\u00e9 shall pay"  -> true: one double space, one decomposed accent
 *   "days\u2019 notice"             "days' notice"           -> true: the apostrophe's form alone differs
 */
function textsReadAlikeWhateverTheirSpacing(leftText: string, rightText: string): boolean {
    return WordTokenizer.readsTheSame(leftText, rightText);
}

/**
 * Whether a paragraph's accepted text holds characters, every one of them a break or
 * spacing: a page, column or line break (each read as "\n"), a tab, a space.
 *
 * EXAMPLE
 *   "\n"          -> true: a page break alone, a spacer between chapters
 *   "\t \n"       -> true
 *   ""            -> false: an empty paragraph holds nothing to strike
 *   "CHAPTER 15"  -> false
 */
function paragraphHoldsOnlyBreaksOrSpacing(acceptedText: string): boolean {
    return acceptedText.length > 0 && acceptedText.trim() === "";
}

export class ProposalBuilder {
    constructor(
        private readonly author: string,
        private readonly stampedAt: string,
    ) {}

    /**
     * Builds the card over the range: the restores once, then the text redline.
     *
     * CASES
     *   - no covered span passed (the web, the core, a card relocated beside its
     *     paragraph)                                 -> one build over the whole paragraphs
     *   - covered spans passed (the add-in's build)  -> the region fork: the offset path and the
     *                                                   whole-paragraph path both build, the referee
     *                                                   picks the result that reads the intended text
     *
     * WHY the reverts go first, and once
     *   A revert card's `textProposed` is the paragraph as it reads with the change
     *   reverted, so the text redline that follows finds the words already there
     *   and writes nothing more; applied the other way round, the text redline
     *   would insert the restored words as new text beside their deletion. Both
     *   paths build from the same reverted package, each over its own copy.
     */
    build(input: ProposalInput): ProposalOutcome {
        const range = new OoxmlRange(input.currentRangeOoxml);
        /**
         * FALLBACK
         *   A caller that hands no card text (the web's revert operations) passes an empty map:
         *   every revert of an insertion keeps one space of the gap it leaves (decision 5).
         */
        const reverted = this.applyReverts(range, input.reverts, input.ownChangeWriting, input.cardTextByBookmark ?? new Map(), input.revertTargetsOfTheRun);
        const revertedRangeOoxml = range.serialize();
        const measuredSpans = input.coveredSpans;
        /**
         * WHEN no span was measured, or the card carries no text it was drafted against
         *   CONSEQUENCE
         *     one build, as today: with no `textsWhenProposed` there is no three-way merge
         *     and so no intended text for a referee to judge by; over a merged paragraph the
         *     whole-paragraph path would then replace the neighbour's words with the card's,
         *     which only the offset path knows to leave alone.
         */
        const textsWhenProposedCanBeAligned = input.textsWhenProposed !== null && input.textsWhenProposed.length === input.textsProposed.length;
        if (measuredSpans === null || measuredSpans.length === 0 || !textsWhenProposedCanBeAligned) {
            const built = this.buildPath({ revertedRangeOoxml, input, measuredSpans, reverted });
            return { packageOoxml: built.packageOoxml, changeCount: built.changeCount, paragraphCount: built.paragraphCount, stampedAt: this.stampedAt, regionVerdict: null };
        }
        const offsetPath = this.timedPath({ revertedRangeOoxml, input, measuredSpans, reverted });
        const wholeParagraphPath = this.timedPath({ revertedRangeOoxml, input, measuredSpans: null, reverted });
        /**
         * The intended text of each paragraph.
         *
         * CASES
         *   - the offset path's span still reads as the card's own text -> the offset path's result: the span
         *                                                                 is sound, and the whole-paragraph merge
         *                                                                 can read a neighbour's words glued to the
         *                                                                 card's as a contested edit and strike them
         *   - otherwise (an empty span, offsets measured before a restore
         *     lengthened the paragraph)                                -> the whole-paragraph path's target: the
         *                                                                 three-way merge over the whole paragraph
         *   - the path that would answer threw                          -> "", which no result reads as
         *
         * EXAMPLE
         *   "Secrets; and (vi) all other rights of the world."Ladafsthis w" means any statute." with the card's span on
         *   the second sentence: the span reads as the card's text, so the offset path's result is intended and the
         *   whole-paragraph path, which strikes "Secrets; …", fails.
         */
        const intendedTexts = Array.from({ length: range.paragraphCount }, (_, index) => {
            const offsetOutcome = offsetPath.outcome;
            if (offsetOutcome !== null && offsetOutcome.spanReadsAsBase[index]) return offsetOutcome.acceptedTexts[index];
            return wholeParagraphPath.outcome?.textsToWrite[index] ?? "";
        });
        const regionVerdict = ProposalRegionReferee.judge({ intendedTexts, offsetPath, wholeParagraphPath });
        const picked = regionVerdict.used === "offset_path" ? offsetPath.outcome : regionVerdict.used === "whole_paragraph_path" ? wholeParagraphPath.outcome : null;
        /**
         * WHEN neither path reads the intended text
         *   CONSEQUENCE
         *     the range comes back as it was read, with no change to write and no mark of an
         *     Apply All run on it (`RevertTargetMarks.strippedFrom`); the add-in tells the user
         *     the change could not be placed exactly.
         */
        if (picked === null) return { packageOoxml: RevertTargetMarks.strippedFrom(input.currentRangeOoxml), changeCount: 0, paragraphCount: range.paragraphCount, stampedAt: this.stampedAt, regionVerdict };
        return { packageOoxml: picked.packageOoxml, changeCount: picked.changeCount, paragraphCount: picked.paragraphCount, stampedAt: this.stampedAt, regionVerdict };
    }

    /**
     * One path of the fork, timed, with any throw caught as the path's failure.
     *
     * CASES
     *   - the path built -> its outcome, its accepted texts and change count for the referee
     *   - the path threw -> no outcome, no texts, the error's message; the referee fails it
     */
    private timedPath(pathInput: RegionPathInput): TimedRegionPath {
        const startedAt = performance.now();
        try {
            const outcome = this.buildPath(pathInput);
            return { outcome, packageOoxml: outcome.packageOoxml, texts: outcome.acceptedTexts, changeCount: outcome.changeCount, buildMs: performance.now() - startedAt, error: null };
        } catch (error) {
            return { outcome: null, packageOoxml: null, texts: [], changeCount: 0, buildMs: performance.now() - startedAt, error: error instanceof Error ? error.message : String(error) };
        }
    }

    /** The text redline of the card over the reverted package, editing the measured spans of the paragraphs, or the whole paragraphs when none are given. */
    private buildPath({ revertedRangeOoxml, input, measuredSpans, reverted }: RegionPathInput): RegionPathBuildOutcome {
        const { author, stampedAt } = this;
        const range = new OoxmlRange(revertedRangeOoxml);
        const allChangesAcceptedTexts = range.allChangesAcceptedTexts();
        /** Read once per build: every paragraph's redline looks its links' addresses up in the same package. */
        const hyperlinkTargets = range.hyperlinkTargetsByRelationshipId();
        /**
         * WHY by the paragraph element, read once here
         *   A paragraph's element stays the same until its own redline replaces it below, so
         *   the element each bookmark names now is the one each paragraph's turn reads.
         */
        const linkInstructionsByParagraph = ProposalBuilder.linkInstructionsByParagraphOf(range, input.linkInstructionsByBookmark);
        /**
         * The part of each live paragraph the text redline covers.
         *
         * CASES
         *   - no span was measured for the paragraph -> the whole paragraph
         *   - a revert rewrote the paragraph         -> the whole paragraph: the span was measured
         *                                               before the reverts, on text they have since
         *                                               lengthened or shortened
         *   - otherwise                              -> the span as measured
         *
         * EXAMPLE
         *   measured span           = { startOffset: 0, endOffset: 60 } on "…laws of England and Wales." (60 chars)
         *   reverts                 restore "the State of New York" (21 chars), strike "England and Wales" (17 chars)
         *   text after the reverts  = "…laws of the State of New York." (64 chars)
         *   slice(0, 60)            -> "…laws of the State of New Y", dropping "ork."
         *   the text redline would then write "ork." a second time: "…New York.ork."
         *   covered whole           -> { startOffset: 0, endOffset: 64 }, and nothing is left to write
         */
        const coveredSpanOf = (text: string, index: number): AcceptedTextSpan => {
            const measuredSpan = measuredSpans?.[index];
            if (measuredSpan === undefined || reverted.paragraphIndexes.has(index)) return { startOffset: 0, endOffset: text.length };
            return measuredSpan;
        };
        const coveredSpans = allChangesAcceptedTexts.map(coveredSpanOf);
        /** The text each live paragraph is edited toward, by index, for the referee's intended texts. */
        const textsToWrite: string[] = [];
        /**
         * EXAMPLE
         *   Covered part only.
         *     text                      = "abcdef"
         *     coveredSpans[index]       = { startOffset: 2, endOffset: 5 }
         *     slice(2, 5)               -> "cde"
         *     kept offsets              = 2, 3, 4
         */
        const textsOnPage = allChangesAcceptedTexts.map((text, index) => text.slice(coveredSpans[index].startOffset, coveredSpans[index].endOffset));
        /**
         * The page each paragraph's three-way merge reads, and the page the card's texts are
         * given to paragraphs by.
         *
         * CASES
         *   - a paragraph the card's reverts rewrote -> its accepted text before the reverts ran,
         *                                               whole: the card's text when proposed was read
         *                                               before them too, so base and page are measured
         *                                               alike and the merge's only change of the card's
         *                                               own is what it adds beyond the revert
         *   - every other paragraph                  -> the page as read
         *
         * WHY the merge reads it
         *   Read after the reverts, the page already held the revert's restored words, and
         *   beside another card's change written since (N27, P6) the two changes made one
         *   contested region, which the merge gives to the card: the revert card took the other
         *   card's change out.
         *
         * EXAMPLE, the merge
         *   text when proposed  "…within thirty days."
         *   text proposed       "…within thirty days of invoice."            (the revert un-strikes " of invoice")
         *   page after reverts  "…within forty-five days of invoice."        -> merge "…thirty days of invoice."
         *   page before them    "…within forty-five days."                   -> merge "…forty-five days of invoice."
         *
         * WHY the card's texts are given to paragraphs by it too
         *   The card's text when proposed is a reading from before the reverts, and it is given
         *   to a paragraph by the paragraph's words, so the step that gives it and the merge read
         *   one page. Given against the page after the reverts, a paragraph a revert emptied whole
         *   read blank like an empty paragraph beside it under the card's bookmark, the
         *   blank-paragraph rule (`absorbEmptyParagraphs`) took the emptied paragraph for the
         *   extra blank one, and gave its text when proposed to the empty paragraph. The merge
         *   then read the struck sentence as words written since the card was proposed, and the
         *   writer inserted it again under the card's author.
         *
         * EXAMPLE, giving the card's texts to paragraphs
         *   card                 textWhenProposed "Supplier's LIABILITY cap will be $1.00 USD.", textProposed ""
         *   read                 the cap paragraph, then an empty paragraph under the card's bookmark
         *   page after reverts   ["", ""]                                              -> text when proposed on the empty paragraph
         *   page the merge reads ["Supplier's LIABILITY cap will be $1.00 USD.", ""]   -> text when proposed on the cap paragraph
         */
        const textsOnPageBeforeReverts = reverted.paragraphIndexes.size === 0 ? textsOnPage : new OoxmlRange(input.currentRangeOoxml).allChangesAcceptedTexts();
        const textsTheMergeReads = textsOnPage.map((textOnPage, index) => (reverted.paragraphIndexes.has(index) ? textsOnPageBeforeReverts[index] : textOnPage));
        /**
         * The paragraphs the card's texts are given to: the document's, not Word's closing paragraph.
         *
         * CASES
         *   - `documentParagraphCount` given (Word's export as given) -> the paragraphs before Word's closing
         *                                                                paragraph, which gets "" and stays
         *   - absent (every other read)                               -> every paragraph of the page
         *
         * WHY not all of them
         *   Word's closing paragraph reads blank, so beside a paragraph another author struck whole it is
         *   one more blank than the card lists, and the blank-paragraph rule (`absorbEmptyParagraphs`)
         *   absorbed the first blank, the struck paragraph's, and gave the card's sentence to Word's.
         *
         * EXAMPLE
         *   page ["", ""] (the cap struck whole, Word's paragraph), documentParagraphCount 1, desired ["Supplier's LIABILITY cap will be $5.00 USD."]
         *   -> the cap paragraph takes the sentence and Word's paragraph "": ["Supplier's LIABILITY cap will be $5.00 USD.", ""]
         */
        const documentParagraphCountOnPage = Math.min(input.documentParagraphCount ?? textsTheMergeReads.length, textsTheMergeReads.length);
        const alignedOverDocumentParagraphs = (textsProposedForParagraphs: string[]): ParagraphAlignment => {
            const documentAlignment = alignDesiredParagraphs(textsTheMergeReads.slice(0, documentParagraphCountOnPage), textsProposedForParagraphs);
            /** Word's closing paragraph, and any after it, is targeted with "": it stays as the write hands it to Word. */
            const closingParagraphTargets = textsTheMergeReads.slice(documentParagraphCountOnPage).map(() => "");
            return { targetTextPerLiveParagraph: [...documentAlignment.targetTextPerLiveParagraph, ...closingParagraphTargets], liveIndexOfDesired: documentAlignment.liveIndexOfDesired };
        };
        const alignment = alignedOverDocumentParagraphs(input.textsProposed);
        const providedTextsWhenProposed = input.textsWhenProposed;
        /**
         * CASES
         *   - no original texts were drafted against        -> null, no three-way merge
         *   - one original text per desired text            -> align them onto the live paragraphs
         *   - counts differ (a stale or partial baseline)   -> null, the desired text is applied as-is
         */
        const textsWhenProposedCanBeAligned = providedTextsWhenProposed !== null && providedTextsWhenProposed.length === input.textsProposed.length;
        const textsWhenProposed = textsWhenProposedCanBeAligned
            ? alignedOverDocumentParagraphs(providedTextsWhenProposed).targetTextPerLiveParagraph
            : null;
        const spanReadsAsBase = textsOnPage.map((textOnPage, index) => textsWhenProposed !== null && textsReadAlikeWhateverTheirSpacing(textOnPage, textsWhenProposed[index]));

        // The redline's text when nothing else changed, otherwise the three-way merge with the live text.
        const textToWriteFor = (textOnPage: string, textWhenProposed: string | null, textProposed: string): string => {
            if (textWhenProposed === null) return textProposed;
            /**
             * WHEN the draft baseline and the live text read alike whatever their spacing
             *   CONSEQUENCE
             *     Use textProposed unchanged.
             *     Differences here are whitespace or Unicode form only, not live edits to merge.
             */
            if (textsReadAlikeWhateverTheirSpacing(textWhenProposed, textOnPage)) return textProposed;
            /** The live text is the document's own: the user's words beside the card's stand beside them (`textOnPageIsTheDocumentsOwn`). */
            return threeWayMerge({ textWhenProposed, textProposed, textOnPage, textOnPageIsTheDocumentsOwn: true });
        };
        const redlineParagraph = (index: number): number => {
            const textToWrite = textToWriteFor(textsTheMergeReads[index], textsWhenProposed?.[index] ?? null, alignment.targetTextPerLiveParagraph[index]);
            textsToWrite[index] = textToWrite;
            const revision = new Revision({ author, stampedAt, firstAllocatedRevisionId: FIRST_REVISION_ID });
            /**
             * WHEN the card deletes whole a paragraph that holds characters but only breaks or
             *      spacing: a page or column break set alone between two chapters, read as "\n"
             *   CONSEQUENCE
             *     its break and its mark are struck together (`OoxmlRange.strikeParagraphWhole`),
             *     one change, and no engine pass: the engine reads "\n" -> "" as spacing only
             *     and writes nothing, so the break stayed and, the mark gone, the next
             *     paragraph joined it once accepted (R34 item 4).
             *
             * WHY the whole paragraph, not the covered span, is read
             *   A span that is part of the paragraph (the user merged a neighbour's words into
             *   it) leaves those words standing, so the paragraph is not deleted whole and its
             *   mark stays (-38's probe on 4fbc04923, 2026-10-19).
             *
             * EXAMPLE
             *   live "\n" (a page break)   target ""     -> break and mark struck
             *   live "\n"                  target "\n"   -> untouched: the card keeps it
             *   live ""                    target ""     -> untouched: nothing to strike
             */
            if (textToWrite === "" && paragraphHoldsOnlyBreaksOrSpacing(allChangesAcceptedTexts[index])) {
                range.strikeParagraphWhole(index, revision);
                return 1;
            }
            const editableSpan = widenSpanForEditedEdges(allChangesAcceptedTexts[index], coveredSpans[index], textToWrite);
            const engine = new RedlineEngine(revision);
            /** NULL: the card names no link of this paragraph. */
            const linkInstructions = linkInstructionsByParagraph.get(range.paragraphElement(index)) ?? NO_LINK_INSTRUCTIONS;
            const outcome = engine.redline({ paragraphXml: range.paragraphXml(index), textToWrite: textToWrite, editableSpan, hyperlinkTargets, linkInstructions, ownChangeWriting: input.ownChangeWriting });
            /**
             * WHEN the engine applied no tracked changes
             *   CONSEQUENCE
             *     Return 0 and leave the paragraph XML as-is.
             */
            if (outcome.appliedChangeCount === 0) return 0;
            range.replaceParagraph(index, outcome.paragraphXml);
            /**
             * WHEN the redline strikes every word of a live paragraph that had words
             *   CONSEQUENCE
             *     Mark its paragraph mark deleted as well, so accepting the deletion
             *     removes the paragraph instead of leaving an empty one behind.
             *
             * WHY the paragraph as written, not the covered span, is read
             *   The card's span can be part of the paragraph: the user merged a neighbour's
             *   sentence into it. A card emptying its own sentence there struck the mark too,
             *   and accepting it in Word joined the neighbours' words to the next paragraph
             *   (-38's probe on 4fbc04923, 2026-10-19). Only a paragraph left with no word
             *   once the redline is written is deleted whole.
             *
             * EXAMPLE
             *   live "None of the following constitutes a waiver."  target ""                    -> mark deleted
             *   live ""                                             target ""                    -> untouched, nothing was deleted
             *   live "Any waiver authorized on one occasion..."     target "Any waiver solely..." -> untouched, words remain
             *   live "Notices go… Customer shall pay… The term is one year.", span on "Customer shall pay…", target ""
             *                                                                                    -> untouched, the neighbours' words remain
             */
            const paragraphDeletedWhole = WordTokenizer.words(new ParagraphModel(range.paragraphElement(index)).text).length === 0 && WordTokenizer.words(allChangesAcceptedTexts[index]).length > 0;
            if (paragraphDeletedWhole) range.markParagraphDeleted(index, revision);
            return outcome.appliedChangeCount;
            /**
             * When the target text begins or ends with words that resemble the words
             * just outside the span, the span is widened to include them. Otherwise the
             * engine would insert a near-copy of a word that already sits next to the
             * span, and the reader would see a duplicate rather than an edit: a
             * bookmark starting after "1.21 " and a redline changing it to "11.21 "
             * must strike "1.21" rather than insert "11.21" beside it.
             *
             * Words here are the redline diff's units (`WordTokenizer.diffUnits`), the
             * engine's own, so a Japanese or Chinese run is several words and the span
             * widens by a word, not by the whole run: "1.21本製品の…" with the bookmark
             * after "1.21" reads "1.21" as the word before the span.
             */
            function widenSpanForEditedEdges(paragraphText: string, span: AcceptedTextSpan, textToWrite: string): AcceptedTextSpan {
                const EDGE_WINDOW_WORDS = 3;
                const EDGE_SIMILARITY = 0.5;

                const commonSubsequenceLength = (left: string, right: string): number => {
                    /**
                     * EXAMPLE
                     *   One cell per prefix of right, including the empty prefix at column 0.
                     *     right.length              = 4
                     *     columns                   = 0, 1, 2, 3, 4
                     *     array length              = 5
                     */
                    const previousRow = new Array<number>(right.length + 1).fill(0);
                    for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
                        let diagonal = 0;
                        for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
                            const above = previousRow[rightIndex];
                            const leftCharacterMatchesRightCharacter = left[leftIndex - 1] === right[rightIndex - 1];
                            /**
                             * CASES
                             *   - characters at these 1-based prefixes match    -> take diagonal + 1
                             *   - characters differ                             -> take max(above, left)
                             * EXAMPLE
                             *   row 3, column 2
                             *     left character            = left[2]
                             *     right character           = right[1]
                             */
                            previousRow[rightIndex] = leftCharacterMatchesRightCharacter ? diagonal + 1 : Math.max(above, previousRow[rightIndex - 1]);
                            diagonal = above;
                        }
                    }
                    return previousRow[right.length];
                };
                // "Contractor" matches "Contractors" and "Contractor's": half the longer word survives as a common subsequence.
                const similarWords = (leftWord: string, rightWord: string): boolean => {
                    const left = WordTokenizer.comparisonKey(leftWord).toLowerCase();
                    const right = WordTokenizer.comparisonKey(rightWord).toLowerCase();
                    /**
                     * WHEN the words are exactly equal
                     *   CONSEQUENCE
                     *     Return true immediately.
                     */
                    if (left === right) return true;
                    return commonSubsequenceLength(left, right) / Math.max(left.length, right.length) >= EDGE_SIMILARITY;
                };

                const allWords = WordTokenizer.diffUnits(paragraphText);
                const insideWords = allWords.filter((token) => token.startOffset >= span.startOffset && token.endOffset <= span.endOffset).map((token) => token.text);
                /**
                 * EXAMPLE
                 *   Last whole words before the span starts.
                 *     earlier words             = 7
                 *     EDGE_WINDOW_WORDS         = 3
                 *     slice(-3)                 -> keep entries 4, 5, 6
                 */
                const wordsBefore = allWords.filter((token) => token.endOffset <= span.startOffset).slice(-EDGE_WINDOW_WORDS);
                /**
                 * EXAMPLE
                 *   First whole words after the span ends.
                 *     later words               = 5
                 *     EDGE_WINDOW_WORDS         = 3
                 *     slice(0, 3)               -> keep entries 0, 1, 2
                 */
                const wordsAfter = allWords.filter((token) => token.startOffset >= span.endOffset).slice(0, EDGE_WINDOW_WORDS);
                const wordsToWrite = WordTokenizer.diffUnits(textToWrite).map((token) => token.text);
                const ops = wordDiff(insideWords.map(WordTokenizer.comparisonKey), wordsToWrite.map(WordTokenizer.comparisonKey));
                let startOffset = span.startOffset;
                let endOffset = span.endOffset;
                /**
                 * WHY
                 *   Only the first diff hunk can be an insertion at the left edge of the span.
                 */
                const firstOp = ops[0];
                /**
                 * CASES
                 *   - no diff at all               -> no
                 *   - the first hunk is not an insert -> no: words were changed or removed at the edge
                 *   - otherwise                    -> yes when it inserts before the span's first live word
                 */
                const firstOpIsLeftEdgeInsert = (): boolean => {
                    const firstDiffOpExists = firstOp !== undefined;
                    if (!firstDiffOpExists) return false;
                    const firstDiffOpIsInsert = firstOp.tag === "insert";
                    if (!firstDiffOpIsInsert) return false;
                    return firstOp.beforeStart === 0;
                };
                /**
                 * WHEN the first diff hunk is an insert at in-span position 0
                 *   CONSEQUENCE
                 *     Compare those inserted words to words just before the span.
                 *     A match means the edit may belong outside the current left edge.
                 */
                if (firstOpIsLeftEdgeInsert()) {
                    /**
                     * EXAMPLE
                     *   Words from this insert hunk only.
                     *     afterStart                = 2
                     *     afterEnd                  = 5
                     *     slice(2, 5)               -> wordsToWrite[2], [3], [4]
                     */
                    const inserted = wordsToWrite.slice(firstOp.afterStart, firstOp.afterEnd);
                    let matched = 0;
                    /**
                     * CASES
                     *   - every inserted word has been matched            -> stop: the whole insertion was already there
                     *   - the words before the span are used up           -> stop: nothing is left to compare against,
                     *                                                        which is every word when the paragraph's
                     *                                                        accepted text is empty (a colleague deleted it)
                     *   - otherwise                                       -> continue while the next pair is similar
                     * EXAMPLE
                     *   Compare both lists from right to left, toward the span boundary.
                     *     wordsBefore.length        = 3
                     *     matched                   = 0  -> wordsBefore[2] against inserted[inserted.length - 1]
                     *     matched                   = 1  -> wordsBefore[1] against inserted[inserted.length - 2]
                     */
                    const leftEdgeWordsStillMatch = (): boolean => {
                        const unmatchedInsertedWordsRemain = matched < inserted.length;
                        if (!unmatchedInsertedWordsRemain) return false;
                        const unmatchedLeadingWordsRemain = matched < wordsBefore.length;
                        if (!unmatchedLeadingWordsRemain) return false;
                        const nextLeadingWordText = wordsBefore[wordsBefore.length - 1 - matched].text;
                        const nextInsertedWord = inserted[inserted.length - 1 - matched];
                        return similarWords(nextLeadingWordText, nextInsertedWord);
                    };
                    while (leftEdgeWordsStillMatch()) matched++;
                    /**
                     * WHEN one or more inserted left-edge words match words before the span
                     *   CONSEQUENCE
                     *     Move startOffset to the first matched outside word.
                     */
                    if (matched > 0) startOffset = wordsBefore[wordsBefore.length - matched].startOffset;
                }
                /**
                 * WHY
                 *   Only the last diff hunk can be an insertion at the right edge of the span.
                 * EXAMPLE
                 *     ops.length                = 4
                 *     ops.length - 1            = 3
                 */
                const lastOp = ops[ops.length - 1];
                /**
                 * CASES
                 *   - no diff at all                 -> no
                 *   - the last hunk is not an insert -> no
                 *   - otherwise                      -> yes when it inserts after the span's last live word
                 */
                const lastOpIsRightEdgeInsert = (): boolean => {
                    const lastDiffOpExists = lastOp !== undefined;
                    if (!lastDiffOpExists) return false;
                    const lastDiffOpIsInsert = lastOp.tag === "insert";
                    if (!lastDiffOpIsInsert) return false;
                    return lastOp.beforeStart === insideWords.length;
                };
                /**
                 * WHEN the last diff hunk is an insert after all in-span words
                 *   CONSEQUENCE
                 *     Compare those inserted words to words just after the span.
                 *     A match means the edit may belong outside the current right edge.
                 */
                if (lastOpIsRightEdgeInsert()) {
                    /**
                     * EXAMPLE
                     *   Words from this trailing insert hunk only.
                     *     afterStart                = 4
                     *     afterEnd                  = 6
                     *     slice(4, 6)               -> wordsToWrite[4], [5]
                     */
                    const inserted = wordsToWrite.slice(lastOp.afterStart, lastOp.afterEnd);
                    let matched = 0;
                    /**
                     * CASES
                     *   - every inserted word has been matched            -> stop: the whole insertion was already there
                     *   - the words after the span are used up            -> stop
                     *   - otherwise                                       -> continue while the next pair is similar
                     */
                    const rightEdgeWordsStillMatch = (): boolean => {
                        const unmatchedInsertedWordsRemain = matched < inserted.length;
                        if (!unmatchedInsertedWordsRemain) return false;
                        const unmatchedTrailingWordsRemain = matched < wordsAfter.length;
                        if (!unmatchedTrailingWordsRemain) return false;
                        const nextTrailingWordText = wordsAfter[matched].text;
                        const nextInsertedWord = inserted[matched];
                        return similarWords(nextTrailingWordText, nextInsertedWord);
                    };
                    while (rightEdgeWordsStillMatch()) matched++;
                    /**
                     * WHEN one or more inserted right-edge words match words after the span
                     *   CONSEQUENCE
                     *     Move endOffset to the end of the last matched outside word.
                     */
                    if (matched > 0) endOffset = wordsAfter[matched - 1].endOffset;
                }
                return { startOffset, endOffset };
            }
        };
        // Inserted paragraphs go after the live paragraph their anchor maps to; `tails` keeps their order when several share an anchor.
        const placeInsertions = (): number => {
            const tails = new Map<number, Element>();
            let placed = 0;
            input.insertions.forEach((insertion, insertionIndex) => {
                const anchorIndex = insertion.afterDesiredIndex < 0
                    ? -1
                    : (alignment.liveIndexOfDesired[insertion.afterDesiredIndex] ?? range.paragraphCount - 1);
                const afterParagraph = anchorIndex < 0 ? tails.get(-1) ?? null : tails.get(anchorIndex) ?? range.paragraphElement(anchorIndex);
                /**
                 * WHEN the inserted paragraph carries no paragraph properties (a text card's insertion)
                 *   CONSEQUENCE
                 *     With `list` "continue" it takes the properties of the live paragraph it
                 *     follows, so a paragraph added inside a numbered list is numbered and indented
                 *     like its neighbour; with "none" it stays a plain paragraph.
                 *
                 * EXAMPLE
                 *   anchorIndex = 1,  paragraphCount = 3 -> template is live paragraph 1
                 *   anchorIndex = -1, paragraphCount = 3 -> a front insertion; template is live paragraph 0
                 *   paragraphCount = 0                   -> no template, Word's default style
                 */
                const templateIndex = anchorIndex < 0 ? 0 : anchorIndex;
                const templateParagraphXml = templateIndex < range.paragraphCount ? range.paragraphXml(templateIndex) : "";
                const inserted = range.insertParagraph({
                    afterParagraph,
                    sourceParagraphXml: ParagraphXml.withParagraphPropertiesFrom({ paragraphXml: insertion.paragraphXml, templateParagraphXml, list: insertion.list }),
                    revision: this.insertedParagraphRevision(insertionIndex),
                });
                /**
                 * WHEN the inserted paragraph carries a bookmark (the id a new paragraph was proposed
                 *      with, or the bookmark of a card paragraph the user deleted, written back)
                 *   CONSEQUENCE
                 *     it is written bookmarked with it.
                 */
                if (insertion.carriedBookmarkName !== undefined && insertion.carriedBookmarkName !== null) range.layBookmarkOnParagraph(inserted, insertion.carriedBookmarkName);
                tails.set(anchorIndex, inserted);
                placed++;
            });
            return placed;
        };

        const paragraphCount = range.paragraphCount;
        let changeCount = reverted.changeCount;
        for (let index = 0; index < paragraphCount; index++) changeCount += redlineParagraph(index);
        changeCount += placeInsertions();
        /**
         * WHEN this package replaces part of a live document
         *   CONSEQUENCE
         *     leave out the markers of any comment it anchors at one end only, or Word
         *     mints a second copy of a comment the document still holds. A whole-document
         *     save keeps every marker it was given.
         */
        if (input.deliveredAs === "rangeReplacement") range.dropCommentMarkersAnchoredOutsideThisRange();
        return { packageOoxml: range.serialize(), changeCount, paragraphCount, acceptedTexts: range.allChangesAcceptedTexts(), textsToWrite, spanReadsAsBase };
        /**
         * Decides which redline paragraph each live paragraph should become.
         *
         * Same count: pair them in order. Extra blank live paragraphs: absorb them.
         * Otherwise diff the two paragraph lists as one stream of the redline diff's
         * units with boundary tokens, compared by the engine's same-word key, and pour
         * the desired units into buckets, one per live paragraph, following the live
         * boundaries: a unit both lists hold goes into the bucket of the live
         * paragraph it sits in, inserted and replacement units into the bucket the
         * cursor is on. `liveIndexOfDesired` records, for each desired paragraph,
         * the live paragraph that received its first unit, so inserted paragraphs
         * can be placed after the right neighbour; a desired paragraph that landed
         * nowhere inherits its predecessor's index.
         */
        function alignDesiredParagraphs(textsOnPage: string[], textsProposed: string[]): ParagraphAlignment {
            /** The token between two paragraphs when several are diffed as one stream. A lone space is never a unit's key. */
            const PARAGRAPH_BOUNDARY: AlignmentToken = { comparisonKey: " ", place: null };
            const liveUnitsPerParagraph = textsOnPage.map((text) => WordTokenizer.diffUnitsWithTrailingSeparators(text));
            const desiredUnitsPerParagraph = textsProposed.map((text) => WordTokenizer.diffUnitsWithTrailingSeparators(text));
            /**
             * What stands between two units poured side by side into one bucket, in
             * the characters a text holds there.
             *
             * CASES
             *   - the two stand side by side in their desired paragraph     -> that paragraph's separator
             *   - they do not (each ends or starts a desired paragraph), and
             *     both matched units standing side by side in one live
             *     paragraph                                              -> the live paragraph's separator
             *   - no text holds them side by side                          -> "": nothing is added between
             *                                                                 them, in any script
             *
             * EXAMPLE
             *   live ["the cat is big"], desired ["the cat", "is big and smart"]: "cat" ends a desired
             *   paragraph and "is" starts the next; both matched live units 1 and 2 of "the cat is big",
             *   so the live " " between them is written: "the cat is big and smart"
             *   live ["Alpha beta."], desired ["Alpha beta.", "Gamma delta."]: "Gamma" matched no live
             *   unit, so no text holds "beta." and "Gamma" side by side: "Alpha beta.Gamma delta."
             */
            const separatorBetweenBucketUnits = (leftBucketUnit: BucketUnit, rightBucketUnit: BucketUnit): string => {
                const leftDesiredPlace = leftBucketUnit.desiredPlace;
                const rightDesiredPlace = rightBucketUnit.desiredPlace;
                const leftDesiredUnit = desiredUnitsPerParagraph[leftDesiredPlace.paragraphIndex][leftDesiredPlace.unitIndex];
                /**
                 * EXAMPLE
                 *   left unit 3 of desired paragraph 0, right unit 4 of the same paragraph -> `3 + 1` = 4, side by side
                 */
                const standSideBySideInDesiredParagraph = rightDesiredPlace.paragraphIndex === leftDesiredPlace.paragraphIndex && rightDesiredPlace.unitIndex === leftDesiredPlace.unitIndex + 1;
                if (standSideBySideInDesiredParagraph) return leftDesiredUnit.trailingSeparator;
                const leftLivePlace = leftBucketUnit.livePlace;
                const rightLivePlace = rightBucketUnit.livePlace;
                /**
                 * EXAMPLE
                 *   left matched live unit 1 of live paragraph 0, right live unit 2 of it -> `1 + 1` = 2, side by side
                 */
                const matchedLiveUnitsStandSideBySide = leftLivePlace !== null && rightLivePlace !== null && rightLivePlace.paragraphIndex === leftLivePlace.paragraphIndex && rightLivePlace.unitIndex === leftLivePlace.unitIndex + 1;
                if (matchedLiveUnitsStandSideBySide) return liveUnitsPerParagraph[leftLivePlace.paragraphIndex][leftLivePlace.unitIndex].trailingSeparator;
                return "";
            };
            /**
             * The text a bucket's units make, in the desired paragraphs' own characters:
             * each unit as the redline spells it, the separator between two units from
             * `separatorBetweenBucketUnits`, the leading whitespace of the desired
             * paragraph whose first unit opens the bucket, and the trailing whitespace of
             * the one whose last unit closes it.
             *
             * EXAMPLE
             *   desired ["1.2\tConfidentiality is reciprocal. Notice is required."] over two live paragraphs
             *   bucket 0 -> "1.2\tConfidentiality is reciprocal."   ("reciprocal." is not its paragraph's last unit: nothing after it)
             *   bucket 1 -> "Notice is required."
             */
            const textOfBucket = (bucketUnits: BucketUnit[]): string => {
                let bucketText = "";
                bucketUnits.forEach((bucketUnit, positionInBucket) => {
                    const { paragraphIndex, unitIndex } = bucketUnit.desiredPlace;
                    const paragraphUnits = desiredUnitsPerParagraph[paragraphIndex];
                    const desiredUnit = paragraphUnits[unitIndex];
                    /**
                     * WHEN the bucket opens with its desired paragraph's first unit
                     *   CONSEQUENCE
                     *     the paragraph's leading whitespace comes first: text before offset `startOffset`
                     */
                    if (positionInBucket === 0 && unitIndex === 0) bucketText += textsProposed[paragraphIndex].slice(0, desiredUnit.startOffset);
                    bucketText += desiredUnit.text;
                    /**
                     * EXAMPLE
                     *   positionInBucket = 2 of 4 units -> `2 + 1` = 3, the unit after it
                     */
                    const nextBucketUnit = bucketUnits[positionInBucket + 1];
                    if (nextBucketUnit !== undefined) {
                        bucketText += separatorBetweenBucketUnits(bucketUnit, nextBucketUnit);
                        return;
                    }
                    /**
                     * WHEN the bucket closes with its desired paragraph's last unit
                     *   CONSEQUENCE
                     *     the paragraph's trailing whitespace follows: text from offset `endOffset` on
                     *
                     * EXAMPLE
                     *   paragraphUnits.length = 5 -> `5 - 1` = 4, the last unit's index
                     */
                    if (unitIndex === paragraphUnits.length - 1) bucketText += textsProposed[paragraphIndex].slice(desiredUnit.endOffset);
                });
                return bucketText;
            };

            // Word adds blank paragraphs around a bookmark freely; when the extra live
            // paragraphs are all blank they are targeted with "" and the rest line up.
            const absorbEmptyParagraphs = (): ParagraphAlignment | null => {
                const extraLiveCount = textsOnPage.length - textsProposed.length;
                /**
                 * WHEN live paragraphs are not in surplus
                 *   CONSEQUENCE
                 *     Return null.
                 * NULL
                 *   No blank-live-paragraph absorption is available here.
                 */
                if (extraLiveCount <= 0) return null;
                const targetTextPerLiveParagraph: string[] = [];
                const liveIndexOfDesired: number[] = [];
                let absorbedCount = 0;
                textsOnPage.forEach((text, liveIndex) => {
                    const liveParagraphHasNoWords = WordTokenizer.words(text).length === 0;
                    const extraBlankParagraphsRemainToAbsorb = absorbedCount < extraLiveCount;
                    /**
                     * The desired paragraph this live paragraph lines up with unless it is absorbed.
                     *
                     * NULL
                     *   undefined once every desired paragraph has its live one: the live paragraphs
                     *   left are the surplus
                     */
                    const nextDesiredText: string | undefined = textsProposed[liveIndexOfDesired.length];
                    const nextDesiredParagraphHasNoWords = nextDesiredText !== undefined && WordTokenizer.words(nextDesiredText).length === 0;
                    /**
                     * WHEN this live paragraph has no words, extra blank paragraphs are still owed, and the
                     *      next desired paragraph has words or none is left
                     *   CONSEQUENCE
                     *     Target this live paragraph with "" and count it as absorbed.
                     *
                     * WHY not when the next desired paragraph is blank too
                     *   That blank is one the card keeps, and this live blank is it: blank pairs with blank
                     *   in order, and only the blanks left over are absorbed. Absorbed instead, every later
                     *   live paragraph took the desired text of the one before it, and the card struck a
                     *   paragraph where it stands and wrote it again on the blank after it.
                     *
                     * EXAMPLE
                     *   live ["", X],                     desired [X]                     -> live 0 absorbed, X on live 1
                     *   live ["Heading", "", "Body", ""], desired ["Heading", "", "Body"] -> "" on live 1, "Body" on live 2,
                     *                                                                       live 3 absorbed
                     */
                    if (liveParagraphHasNoWords && extraBlankParagraphsRemainToAbsorb && !nextDesiredParagraphHasNoWords) {
                        targetTextPerLiveParagraph.push("");
                        absorbedCount++;
                        return;
                    }
                    targetTextPerLiveParagraph.push(textsProposed[liveIndexOfDesired.length] ?? "");
                    liveIndexOfDesired.push(liveIndex);
                });
                /**
                 * CASES
                 *   - absorbedCount === extraLiveCount  -> return the recovered alignment
                 *   - absorbedCount <  extraLiveCount   -> return null
                 * NULL
                 *   Not all surplus live paragraphs were blank.
                 *   Caller must fall back to full token diffing.
                 */
                return absorbedCount === extraLiveCount ? { targetTextPerLiveParagraph, liveIndexOfDesired } : null;
            };
            /**
             * The paragraphs' units as one stream, with a boundary token between two
             * paragraphs, each unit keyed by the engine's same-word key.
             *
             * EXAMPLE
             *   ["the cat", "is big"] -> the(0,0) cat(0,1) | is(1,0) big(1,1)
             */
            const tokensWithBoundaries = (unitsPerParagraph: DiffUnitWithTrailingSeparator[][]): AlignmentToken[] =>
                unitsPerParagraph.flatMap((paragraphUnits, paragraphIndex) => {
                    const paragraphTokens = paragraphUnits.map((unit, unitIndex): AlignmentToken => ({ comparisonKey: WordTokenizer.comparisonKey(unit.text), place: { paragraphIndex, unitIndex } }));
                    /**
                     * CASES
                     *   - the first paragraph -> its units alone
                     *   - any later one       -> a boundary token, then its units
                     */
                    return paragraphIndex > 0 ? [PARAGRAPH_BOUNDARY, ...paragraphTokens] : paragraphTokens;
                });

            /**
             * WHEN live and desired paragraph counts are equal
             *   CONSEQUENCE
             *     Map paragraphs one-to-one by index.
             */
            if (textsOnPage.length === textsProposed.length) {
                return { targetTextPerLiveParagraph: textsProposed, liveIndexOfDesired: textsProposed.map((_, index) => index) };
            }
            /**
             * WHEN the range holds no paragraph at all
             *   CONSEQUENCE
             *     no live paragraph can take the desired words, and every inserted paragraph
             *     goes to the front (-1); the build writes the insertions and nothing else,
             *     instead of failing on a bucket that does not exist.
             */
            if (textsOnPage.length === 0) return { targetTextPerLiveParagraph: [], liveIndexOfDesired: textsProposed.map(() => -1) };
            const absorbed = absorbEmptyParagraphs();
            /**
             * WHEN blank-paragraph absorption produced an alignment
             *   CONSEQUENCE
             *     Return that alignment.
             * NULL
             *   A null absorbed value means absorption failed, so continue to token diffing.
             */
            if (absorbed) return absorbed;
            const liveTokens = tokensWithBoundaries(liveUnitsPerParagraph);
            const desiredTokens = tokensWithBoundaries(desiredUnitsPerParagraph);
            const desiredIndexAtToken: number[] = [];
            let desiredIndex = 0;
            for (const token of desiredTokens) {
                desiredIndexAtToken.push(desiredIndex);
                /**
                 * WHEN the flattened desired token is a paragraph boundary
                 *   CONSEQUENCE
                 *     Advance to the next desired paragraph index.
                 */
                if (token.place === null) desiredIndex++;
            }
            const buckets: BucketUnit[][] = textsOnPage.map(() => []);
            const liveIndexOfDesired: number[] = textsProposed.map(() => -1);
            let liveCursor = 0;
            const recordLanding = (desiredTokenIndex: number): void => {
                const index = desiredIndexAtToken[desiredTokenIndex];
                const desiredParagraphHasNoRecordedLanding = liveIndexOfDesired[index] === -1;
                /**
                 * WHEN this desired paragraph has no recorded landing yet
                 *   CONSEQUENCE
                 *     Record the current live paragraph as its first landing.
                 */
                if (desiredParagraphHasNoRecordedLanding) liveIndexOfDesired[index] = liveCursor;
            };
            /**
             * EXAMPLE
             *   Count boundaries in the half-open range [start, end).
             *     start                     = 3
             *     end                       = 7
             *     slice(3, 7)               -> inspect indices 3, 4, 5, 6
             */
            const boundariesIn = (start: number, end: number): number =>
                liveTokens.slice(start, end).filter((token) => token.place === null).length;
            const pourDesired = (start: number, end: number): void => {
                for (let tokenIndex = start; tokenIndex < end; tokenIndex++) {
                    const desiredPlace = desiredTokens[tokenIndex].place;
                    /**
                     * WHEN the desired token is a synthetic paragraph boundary
                     *   CONSEQUENCE
                     *     Skip it.
                     *     Only real words go into buckets.
                     */
                    if (desiredPlace === null) continue;
                    buckets[liveCursor].push({ desiredPlace, livePlace: null });
                    recordLanding(tokenIndex);
                }
            };
            for (const op of wordDiff(
                liveTokens.map((token) => token.comparisonKey),
                desiredTokens.map((token) => token.comparisonKey),
            )) {
                /**
                 * WHEN the diff hunk is equal
                 *   CONSEQUENCE
                 *     Copy unchanged words into their current live paragraph buckets,
                 *     as the redline spells them, each remembering the live unit it matched.
                 */
                if (op.tag === "equal") {
                    for (let tokenIndex = op.beforeStart; tokenIndex < op.beforeEnd; tokenIndex++) {
                        const livePlace = liveTokens[tokenIndex].place;
                        /**
                         * WHEN an unchanged token is a paragraph boundary
                         *   CONSEQUENCE
                         *     Advance liveCursor to the next live paragraph.
                         */
                        if (livePlace === null) {
                            liveCursor++;
                            /**
                             * WHEN the boundary already advanced liveCursor
                             *   CONSEQUENCE
                             *     Skip adding the boundary token to any bucket.
                             */
                            continue;
                        }
                        /**
                         * EXAMPLE
                         *   equal live tokens 4..7 against desired tokens 2..5: live token 6 -> desired token `2 + (6 - 4)` = 4
                         */
                        const desiredTokenIndex = op.afterStart + (tokenIndex - op.beforeStart);
                        const desiredPlace = desiredTokens[desiredTokenIndex].place;
                        /**
                         * WHEN the matched desired token is a boundary
                         *   CONSEQUENCE
                         *     nothing to pour: a boundary's key matches only a boundary, so this never
                         *     holds for a live unit, and the guard only keeps the bucket to units
                         */
                        if (desiredPlace === null) continue;
                        buckets[liveCursor].push({ desiredPlace, livePlace });
                        recordLanding(desiredTokenIndex);
                    }
                    /**
                     * WHEN the equal hunk is fully handled
                     *   CONSEQUENCE
                     *     Skip the insert/delete/replace logic below.
                     */
                    continue;
                }
                /**
                 * WHEN the diff hunk is insert
                 *   CONSEQUENCE
                 *     Pour the desired words into the bucket at the current live cursor.
                 */
                if (op.tag === "insert") {
                    pourDesired(op.afterStart, op.afterEnd);
                    /**
                     * WHEN the insert hunk is fully handled
                     *   CONSEQUENCE
                     *     Skip the delete/replace logic below.
                     */
                    continue;
                }
                // A deleted or replaced hunk may begin with paragraph boundaries: those
                // live paragraphs received nothing, so the cursor moves past them before
                // the replacement words are poured, and any boundary further inside the
                // hunk advances the cursor after them, so the words land in the first
                // live paragraph the hunk touches rather than the last.
                let firstNonBoundary = op.beforeStart;
                /**
                 * CASES
                 *   - the hunk's live range is used up     -> stop: it held only boundaries
                 *   - otherwise                            -> step over the next token when it is a paragraph boundary
                 */
                const nextLiveTokenIsParagraphBoundaryWithinOp = (): boolean => {
                    const moreTokensRemainInOp = firstNonBoundary < op.beforeEnd;
                    if (!moreTokensRemainInOp) return false;
                    return liveTokens[firstNonBoundary].place === null;
                };
                while (nextLiveTokenIsParagraphBoundaryWithinOp()) firstNonBoundary++;
                liveCursor += firstNonBoundary - op.beforeStart;
                /**
                 * CASES
                 *   - op.tag === "replace"      -> pour desired words for the replacement
                 *   - op.tag === "delete"       -> pour nothing
                 */
                if (op.tag === "replace") pourDesired(op.afterStart, op.afterEnd);
                liveCursor += boundariesIn(firstNonBoundary, op.beforeEnd);
            }
            let previousLanding = 0;
            for (let index = 0; index < liveIndexOfDesired.length; index++) {
                const desiredParagraphHasNoLanding = liveIndexOfDesired[index] === -1;
                /**
                 * WHEN this desired paragraph never landed in any live paragraph
                 *   CONSEQUENCE
                 *     Reuse the previous desired paragraph's landing.
                 */
                if (desiredParagraphHasNoLanding) liveIndexOfDesired[index] = previousLanding;
                previousLanding = liveIndexOfDesired[index];
            }
            return { targetTextPerLiveParagraph: buckets.map((bucketUnits) => textOfBucket(bucketUnits)), liveIndexOfDesired };
        }
    }

    /**
     * The card's link instructions by the live paragraph element they are for, found by
     * the bookmarks the range's paragraphs carry (`OoxmlRange.paragraphElementsByBookmarkName`).
     *
     * EMPTY
     *   The caller hands no instruction, or none for a paragraph of the range.
     *
     * EXAMPLE
     *   { "_result_prev_signed" => [keep "the Fee Schedule"] }, the range's second paragraph carries
     *   `_result_prev_signed` -> { <second paragraph> => [keep "the Fee Schedule"] }
     */
    private static linkInstructionsByParagraphOf(range: OoxmlRange, linkInstructionsByBookmark: ReadonlyMap<string, readonly LinkInstruction[]> | undefined): Map<Element, readonly LinkInstruction[]> {
        const linkInstructionsByParagraph = new Map<Element, readonly LinkInstruction[]>();
        /** EMPTY: the caller hands no instruction. */
        if (linkInstructionsByBookmark === undefined || linkInstructionsByBookmark.size === 0) return linkInstructionsByParagraph;
        for (const [bookmarkName, paragraph] of range.paragraphElementsByBookmarkName()) {
            const linkInstructions = linkInstructionsByBookmark.get(bookmarkName);
            /** WHEN the card names links of this paragraph CONSEQUENCE they ride with its element. */
            if (linkInstructions !== undefined) linkInstructionsByParagraph.set(paragraph, linkInstructions);
        }
        return linkInstructionsByParagraph;
    }

    /**
     * Applies every revert whose paragraph the range holds, each through
     * `RedlineEngine.revertChange` on a revision band of its own, and replaces
     * the paragraph with the result.
     *
     * `cardTextByBookmark` hands each revert the text its card writes in that paragraph
     * once the reverts are done (`RevertChangeRequest.textTheCardWritesAfter`), so a revert
     * of an insertion strikes the insertion alone when the card writes words into the gap
     * it leaves (§17, FREEZE FIX #4).
     *
     * EXAMPLE, cardTextByBookmark
     *   { "_pay" => "…within forty-five (45) days of receipt." }, revert of Counsel's "sixty (60)" on _pay
     *   -> the revert strikes "sixty (60)" alone; "forty-five (45)" is written between the plain spaces
     *   {} (no card text)
     *   -> decision 5: one of the two spaces goes with the insertion
     *
     * CASES
     *   - the revert's bookmark names a paragraph of the range          -> reverted in place
     *   - it names none (the paragraph left the range)                  -> skipped: the text redline that follows still
     *                                                                      writes the paragraph's proposed text, so the
     *                                                                      words come back as new text beside their deletion
     *   - the engine refuses (the change is gone or reads differently)  -> the refusal propagates: the card cannot do what
     *                                                                      it says, and the caller reports why
     *
     * EXAMPLE
     *   revertIndex = 1 -> ids from 8500 + 1 × 50 = 8550, clear of the first revert's band
     *
     * WHY every revert's change is marked before the first is written
     *   Each revert adds a change to its paragraph and moves the ids after it, so the second
     *   of two reverts in one paragraph, resolved by its id, named another change ("reserved"
     *   in (d) and (f) of §10.1). Each change is found on the range as handed in and marked on
     *   its own elements (`RevertTargetMarks.layOn`), and every revert is resolved by its mark.
     *
     * CASES, for the marks
     *   - the Apply All run marked the page (`revertTargetsOfTheRun`) -> resolved by the run's marks,
     *                                                                   which stay for the cards after
     *   - otherwise                                                   -> marked here, and taken off once
     *                                                                   the reverts are written or one
     *                                                                   throws, so no package the build
     *                                                                   returns carries one
     *
     * RETURNS
     *   The changes the reverts applied, counted into the outcome's `changeCount`.
     */
    private applyReverts(range: OoxmlRange, reverts: RedlineTextRevert[], ownChangeWriting: OwnChangeWriting, cardTextByBookmark: ReadonlyMap<string, string>, revertTargetsOfTheRun: RevertTargetsOfTheRun | undefined): AppliedReverts {
        const applied: AppliedReverts = { changeCount: 0, paragraphIndexes: new Set<number>() };
        /** WHEN the card reverts nothing CONSEQUENCE nothing to mark or write. */
        if (reverts.length === 0) return applied;
        /** WHEN the Apply All run already marked the range CONSEQUENCE its marks resolve every revert, and stay. */
        if (revertTargetsOfTheRun !== undefined) {
            this.writeReverts({ range, reverts, ownChangeWriting, cardTextByBookmark, targetsOfTheRun: revertTargetsOfTheRun, applied });
            return applied;
        }
        const laidHere = RevertTargetMarks.layOn(range, reverts);
        try {
            this.writeReverts({ range, reverts, ownChangeWriting, cardTextByBookmark, targetsOfTheRun: laidHere.targetsOfTheRun, applied });
        } finally {
            RevertTargetMarks.stripFrom(range, laidHere.revertNamesLaidHere);
        }
        return applied;
    }

    /** Writes each revert on its paragraph, resolved by the marks of `targetsOfTheRun`, adding what it applied to `applied`. */
    private writeReverts({ range, reverts, ownChangeWriting, cardTextByBookmark, targetsOfTheRun, applied }: RevertsToWrite): void {
        /** The revert's own redline of its restored words looks link addresses up in the package, as the text redline does. */
        const hyperlinkTargets = range.hyperlinkTargetsByRelationshipId();
        reverts.forEach((revert, revertIndex) => {
            /**
             * WHY the lookup is made per revert
             *   A replaced paragraph is a new element, so a map built once would not find
             *   a paragraph two reverts name.
             */
            const paragraph = range.paragraphElementsByBookmarkName().get(revert.bookmark);
            if (paragraph === undefined) return;
            const paragraphIndex = Array.from({ length: range.paragraphCount }, (_, index) => index).find((index) => range.paragraphElement(index) === paragraph);
            if (paragraphIndex === undefined) return;
            const revision = new Revision({ author: this.author, stampedAt: this.stampedAt, firstAllocatedRevisionId: REVERT_FIRST_REVISION_ID + revertIndex * REVERT_REVISION_ID_STEP });
            const outcome = new RedlineEngine(revision).revertChange({
                paragraphXml: range.paragraphXml(paragraphIndex),
                changeId: revert.changeId,
                expectedChange: revert.expectedChange,
                text: revert.text,
                ownChangeWriting,
                hyperlinkTargets,
                /** NULL when the card names no text for the revert's paragraph: decision 5 holds. */
                textTheCardWritesAfter: cardTextByBookmark.get(revert.bookmark) ?? null,
                markOfTheRun: RevertTargetMarks.markOf(revert, targetsOfTheRun),
            });
            range.replaceParagraph(paragraphIndex, outcome.paragraphXml);
            applied.changeCount += outcome.appliedChangeCount;
            applied.paragraphIndexes.add(paragraphIndex);
        });
    }

    /** A package that inserts these paragraphs whole, each marked as an insertion, for placement after a range with no live counterpart. */
    insertedParagraphsPackage(paragraphXmls: string[]): string {
        const target = new OoxmlRange(OoxmlRange.packageAround(""));
        let previous: Element | null = null;
        paragraphXmls.forEach((sourceParagraphXml, index) => {
            const insertedParagraphSpec = {
                afterParagraph: previous,
                sourceParagraphXml,
                revision: this.insertedParagraphRevision(index),
            };
            previous = target.insertParagraph(insertedParagraphSpec);
        });
        return target.serialize();
    }

    /** Inserted paragraphs take ids from 9100 in steps of 50 so they never collide with the engine's ids inside edited paragraphs. */
    private insertedParagraphRevision(insertionIndex: number): Revision {
        const INSERTED_PARAGRAPH_FIRST_ID = 9100;
        const INSERTED_PARAGRAPH_ID_STEP = 50;
        return new Revision({
            author: this.author,
            stampedAt: this.stampedAt,
            firstAllocatedRevisionId: INSERTED_PARAGRAPH_FIRST_ID + insertionIndex * INSERTED_PARAGRAPH_ID_STEP,
        });
    }
}
